import type {
  AIModelChoices,
  AluminiumComposition,
  DetectedUbcContent,
  EvidenceUploadMode,
  SimpleMaterialQualityCategory,
} from 'api';
import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { Link } from 'react-router-dom';
import { Box, DataCard, Stack } from 'ui';
import { Insights } from 'ui/icons';
import { AdminAiVersionTitle } from '../../../breakdowns/components/AdminAiVersionTitle/AdminAiVersionTitle';
import { AluminiumCompositionBreakdown } from '../../../breakdowns/components/AluminiumCompositionBreakdown/AluminiumCompositionBreakdown';
import { NoAnalysisAlert } from '../../../breakdowns/components/NoAnalysisAlert/NoAnalysisAlert';
import { RepresentativeEvidenceFilter } from '../../../breakdowns/components/RepresentativeEvidenceFilter/RepresentativeEvidenceFilter';
import { useGetRepresentativeEvidence } from '../../../breakdowns/hooks/useGetRepresentativeEvidence/useGetRepresentativeEvidence';
import { formatAiModel } from '../../../breakdowns/utils/formatAiModel/formatAiModel';
import { TargetBaleWeightsHistogram } from '../../../charts';
import { PhotoEvidenceGalleryWithDialog } from '../../../evidence';

type Props = {
  aluminiumComposition?: AluminiumComposition;
  isLoading: boolean;
  aiModel: AIModelChoices;
  showAerosol?: boolean;
  detectedUbcContent?: DetectedUbcContent;
  parentId?: number;
  uploadMode?: EvidenceUploadMode;
  sessionLink?: string;
  sessionName?: string;
  latestRunAIVersion?: string;
  materialCategory?: Pick<SimpleMaterialQualityCategory, 'id' | 'name'> | null;
};

export const AluminiumCompositionDataCard = ({
  aluminiumComposition,
  isLoading,
  aiModel,
  showAerosol = true,
  detectedUbcContent,
  parentId,
  uploadMode,
  sessionLink,
  sessionName,
  latestRunAIVersion,
  materialCategory,
}: Props) => {
  const { t } = useTranslation('features');
  const title = `${formatAiModel(aiModel)} Analysis`;

  const { representativeType, setRepresentativeType, representativeEvidence } = useGetRepresentativeEvidence({
    detectedContent: detectedUbcContent,
    parentId: parentId || 0,
    uploadMode,
  });

  if (!aluminiumComposition && !detectedUbcContent) {
    logger.warn('No AI Analysis found');
  }

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          avatar: <Insights data-testid="InsightsIcon" />,
          title: <AdminAiVersionTitle title={title} version={latestRunAIVersion} />,
        },
      }}
    >
      {sessionLink && sessionName ? (
        <DataCard.Item title={t('dataCards.aluminiumComposition.reportTitle')} colSpan={6}>
          <Link to={sessionLink}>{sessionName}</Link>
        </DataCard.Item>
      ) : null}
      <DataCard.Item colSpan={12}>
        {aluminiumComposition ? (
          <AluminiumCompositionBreakdown composition={aluminiumComposition} showAerosol={showAerosol} />
        ) : (
          <NoAnalysisAlert aiModel={aiModel} />
        )}
      </DataCard.Item>
      {detectedUbcContent ? (
        <DataCard.Item colSpan={12}>
          <TargetBaleWeightsHistogram
            targetBaleWeights={detectedUbcContent.target_bale_weights}
            targetGrade={detectedUbcContent.average_target_content * 100}
            title={t('dataCards.aluminiumComposition.targetDistributionTitle')}
          />
          <Box
            sx={{
              display: 'flex',
              mt: 2,
            }}
          >
            <RepresentativeEvidenceFilter value={representativeType} onChange={setRepresentativeType} />
          </Box>
          {representativeEvidence?.length ? (
            <Stack
              direction="column"
              sx={{
                gap: 1,
                mt: 1,
              }}
            >
              <PhotoEvidenceGalleryWithDialog evidence={representativeEvidence} materialCategory={materialCategory} />
            </Stack>
          ) : null}
        </DataCard.Item>
      ) : null}
    </DataCard>
  );
};
